// @ts-nocheck
import {
  CheckboxProps,
  DefaultMantineColor,
  MantineThemeOverride,
  Tuple,
} from "@mantine/core";
import { MdOutlineCheck, MdOutlineRemove } from "react-icons/md";
import { getButtonLabelStyle, getButtonStyle } from "./button";
import { fontSizes, textStyleMapping } from "./typography";

type ExtendedCustomColors = "brand" | DefaultMantineColor;

declare module "@mantine/core" {
  // @ts-ignore mo ned
  export interface MantineThemeColorsOverride {
    colors: Record<ExtendedCustomColors, Tuple<string, 10>>;
  }
}

const CheckboxIconOverride: CheckboxProps["icon"] = ({
  className,
  indeterminate,
}) =>
  indeterminate ? (
    <MdOutlineRemove className={className} />
  ) : (
    <MdOutlineCheck className={className} />
  );

export const inputErrorDefaultProps = {
  ...textStyleMapping,
  mt: 3,
  weight: 600,
};

export const theme: MantineThemeOverride = {
  fontFamily: "Roboto, sans-serif",
  spacing: {
    xs: 8,
    sm: 12,
    md: 16,
  },
  fontSizes,
  headings: {
    fontFamily: "Roboto, sans-serif",
    sizes: {
      h1: {
        fontSize: 60,
        fontWeight: 700,
        lineHeight: 1.2,
      },
      h2: {
        fontSize: 48,
        fontWeight: 700,
        lineHeight: 1.16,
      },
      h3: {
        fontSize: 34,
        fontWeight: 800,
        lineHeight: 1.16,
      },
    },
  },
  black: "#1D1929",
  primaryShade: 8,
  colors: {
    brand: [
      "#e6ecff",
      "#ccd8ff",
      "#b3c5ff",
      "#668aff",
      "#4d77ff",
      "#3363ff",
      "#1a50ff",
      "#003cff",
      "#0066cc",
      "#0040cc",
    ],
    orange: [
      "#fff1e6",
      "#ffd4b3",
      "#ffc599",
      "#ffb780",
      "#ffa867",
      "#ff8b34",
      "#ff7d1a",
      "#ff6e01",
      "#e66301",
      "#cc5801",
    ],
    red: [
      "#feeaea",
      "#fcbfbf",
      "#fba9a9",
      "#fa9494",
      "#f76969",
      "#f65354",
      "#f53e3e",
      "#f42829",
      "#dc2425",
      "#c32021",
    ],
    green: [
      "#e9fbf8",
      "#bcf8e9",
      "#a5f6e2",
      "#78f2d3",
      "#62f0cc",
      "#4bedc5",
      "#35ebbd",
      "#1ee9b6",
      "#1bd2a4",
      "#18ba92",
    ],
    yellow: [
      "#fffcee",
      "#fff5cc",
      "#fff2bb",
      "#ffefaa",
      "#ffe887",
      "#ffe576",
      "#ffe165",
      "#ffde54",
      "#f9ef00",
      "#ccb243",
    ],
  },
  primaryColor: "brand",
  defaultRadius: "xl",
  components: {
    Select: {
      defaultProps: {
        errorProps: inputErrorDefaultProps,
      },
    },
    Modal: {
      defaultProps: {
        centered: true,
        withCloseButton: false,
        overlayOpacity: 0.55,
        overlayBlur: 3,
        radius: 32,
        padding: 48,
      },
    },
    Checkbox: {
      defaultProps: {
        size: "md",
        radius: "xl",
        icon: CheckboxIconOverride,
      },
      styles: (t) => ({
        root: {
          display: "flex",
        },
        input: { cursor: "pointer", borderWidth: 2 },
        label: {
          fontSize: t.fontSizes.sm,
          fontWeight: 600,
          color: t.colors.gray[8],
        },
      }),
    },
    Container: {
      defaultProps: {
        size: 1296,
      },
      styles: () => ({
        root: { width: "100%" },
      }),
    },
    Header: {
      styles: () => ({
        root: {
          display: "flex",
        },
      }),
    },
    Title: {
      defaultProps: {
        size: "h3",
        weight: 800,
      },
      styles: {
        root: {
          "&:is(h1)": {
            letterSpacing: "-0.005em",
          },
        },
      },
    },
    Text: {
      defaultProps: {
        size: "md",
        color: "#1D1929",
      },
    },
    DatePicker: {
      defaultProps: {
        errorProps: inputErrorDefaultProps,
      },
      styles: (t) => ({
        input: {
          padding: 24,
          fontSize: 16,
          borderColor: t.colors.gray[2],
        },
      }),
    },
    PasswordInput: {
      defaultProps: {
        errorProps: inputErrorDefaultProps,
      },
      styles: (t) => ({
        input: {
          borderColor: t.colors.gray[2],
          padding: 24,
        },
        innerInput: {
          padding: 24,
        },
      }),
    },
    TextInput: {
      defaultProps: {
        errorProps: inputErrorDefaultProps,
      },
      styles: (t) => ({
        input: {
          padding: 24,
          borderColor: t.colors.gray[2],
        },
      }),
    },
    NumberInput: {
      defaultProps: {
        errorProps: inputErrorDefaultProps,
      },
      styles: (t) => ({
        input: {
          padding: 24,
          borderColor: t.colors.gray[2],
        },
      }),
    },
    Textarea: {
      defaultProps: {
        radius: 18,
      },
    },
    Paper: {
      defaultProps: {
        radius: 24,
      },
      styles: (t) => ({
        root: {
          borderColor: t.colors.gray[1],
        },
      }),
    },
    Card: {
      defaultProps: {
        radius: 32,
        p: 64,
      },
    },
    Button: {
      defaultProps: {
        size: "md",
      },
      styles: (t, params) => ({
        root: getButtonStyle(t, params),
        label: getButtonLabelStyle(t, params),
      }),
    },
    Menu: {
      defaultProps: {
        radius: 18,
      },
    },
    Radio: {
      styles: (t) => ({
        label: {
          flex: 1,
          fontWeight: 700,
          fontSize: 14,
        },
        radio: {
          cursor: "pointer",
          "&:disabled": {
            backgroundColor: t.colors.gray[0],
          },
        },
      }),
    },
    RadioGroup: {
      defaultProps: {
        errorProps: inputErrorDefaultProps,
      },
      styles: {
        error: {
          fontSize: 12,
        },
      },
    },
  },
};
